import { useCallback } from "react";
import { Button, Select, Space, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { ConditionAction, ModeOfFallbackCreation, NewFallbackButtonType, NewFallbackStep } from "@/constants/FallbackEnum";
import { fieldOptions } from "@/constants/Fallback";
import { FilterCondition } from './types';
import { getConditionLabelAsCompressorFailure, handleConditionChange } from './Fallbacks.utils';
import { PageContainerForCreateNewDraftFallback } from './styled';

const { Option } = Select;
const { Title, Text } = Typography;

interface RenderFilterStepProps {
    t: (key: string) => string;
    conditions: FilterCondition[];
    compressorOptions: Array<{ label: string; value: string }>;
    shouldShowAddButton: boolean;
    updateConditionHandler: (id: number, key: keyof FilterCondition, val) => void;
    removeConditionHandler: (id: number) => void;
    addConditionHandler: () => void;
    handleApplyFilters: () => void;
}

export const RenderFilterStep = ({
    t,
    conditions,
    compressorOptions,
    shouldShowAddButton,
    updateConditionHandler,
    removeConditionHandler,
    addConditionHandler,
    handleApplyFilters,
}: RenderFilterStepProps) => {
    const handleConditionChangeWrapper = useCallback((cond: FilterCondition, action: ConditionAction, val) => {
        handleConditionChange(cond, action, val, updateConditionHandler, removeConditionHandler);
    }, [updateConditionHandler, removeConditionHandler]);

    const handleAddConditionClick = useCallback(() => {
        addConditionHandler();
    }, [addConditionHandler]);

    const createChangeHandler = useCallback((cond: FilterCondition, action: ConditionAction) => {
        return (val?) => handleConditionChangeWrapper(cond, action, val);
    }, [handleConditionChangeWrapper]);

    return (
    <PageContainerForCreateNewDraftFallback>
        <div className="content-section">
            <Title level={4}>
                {t("createANewFallbackScenario")}
            </Title>
            <Text className="modal-subtitle">
                {t("firstApplyFiltersToDefineYourScenario")}
            </Text>

            <div className="filter-content">
                <div className="full-width-space">
                    {conditions.map((cond, index) => (
                        <div className="filter-condition-container" key={cond.id}>
                            <div className="filter-condition-content">
                                {index > 0 && (
                                    <div className="and-condition-container">
                                        <div className="and-label">{t('AND')}</div>
                                        <Select
                                            className="full-width-select"
                                            placeholder={t('selectScenario')}
                                            value={cond.field || undefined}
                                            onChange={createChangeHandler(cond, ConditionAction.Field)}
                                        >
                                            {fieldOptions
                                                .filter((opt) => !conditions.some((c) => c.field === opt.value))
                                                .map((opt) => (
                                                    <Option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </Option>
                                                ))}
                                        </Select>
                                    </div>
                                )}

                                {index === 0 && (
                                    <Select
                                        className="full-width-select"
                                        placeholder={t('selectScenario')}
                                        value={cond.field || undefined}
                                        onChange={createChangeHandler(cond, ConditionAction.Field)}
                                    >
                                        {fieldOptions.map((opt) => (
                                            <Option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </Option>
                                        ))}
                                    </Select>
                                )}

                                {getConditionLabelAsCompressorFailure(cond) && (
                                    <Select
                                        className="compressor-width-select"
                                        mode="multiple"
                                        placeholder={t('selectCompressors')}
                                        value={cond.value as string[]}
                                        onChange={createChangeHandler(cond, ConditionAction.Value)}
                                    >
                                        {compressorOptions?.map((opt) => (
                                            <Option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            </div>

                            {conditions.length > 1 && (
                                <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    onClick={createChangeHandler(cond, ConditionAction.Remove)}
                                />
                            )}
                        </div>
                    ))}

                    {shouldShowAddButton && (
                        <div className="add-condition-button" onClick={handleAddConditionClick}>
                            + {t('add')}
                        </div>
                    )}
                </div>
            </div>

            <Button
                className="create-button-for-create-new-draft-fallback"
                onClick={handleApplyFilters}
                disabled={!conditions.some((c) => c.field)}
            >
                {t("applyFiltersAndContinue")}
            </Button>
        </div>
    </PageContainerForCreateNewDraftFallback>
    );
};

interface RenderTableSelectionStepProps {
    t: (key: string) => string;
    mode: ModeOfFallbackCreation | null;
    scenarioName: string;
    tablesToShow: Array<{ id: string; name: string }>;
    isExistingMode: boolean;
    isSmartMode: boolean;
    selectedRangeParentKey: string;
    setMode: (mode: ModeOfFallbackCreation) => void;
    setScenarioName: (name: string) => void;
    setSelectedRangeParentKey: (key: string) => void;
    setCurrentStep: (step: NewFallbackStep) => void;
    handleCreate: () => void;
}

export const RenderTableSelectionStep = ({
    t,
    mode,
    scenarioName,
    tablesToShow,
    isExistingMode,
    isSmartMode,
    selectedRangeParentKey,
    setMode,
    setScenarioName,
    setSelectedRangeParentKey,
    setCurrentStep,
    handleCreate
}: RenderTableSelectionStepProps) => {
    const handleCustomModeClick = useCallback(() => {
        setMode(ModeOfFallbackCreation.Custom);
    }, [setMode]);

    const handleExistingModeClick = useCallback(() => {
        setMode(ModeOfFallbackCreation.Existing);
    }, [setMode]);

    const handleScenarioChange = useCallback((value: string) => {
        const selectedScenario = tablesToShow?.find(scenario => scenario.id === value);
        setScenarioName(selectedScenario?.name || "");
        setSelectedRangeParentKey(selectedScenario?.id || "");
    }, [tablesToShow, setScenarioName, setSelectedRangeParentKey]);

    const handleBackClick = useCallback(() => {
        setCurrentStep(NewFallbackStep.Filters);
    }, [setCurrentStep]);

    return (
    <PageContainerForCreateNewDraftFallback>
        <div className="content-section">
            <Title level={4}>
                {t("createANewFallbackScenario")}
            </Title>
            <Text className="modal-subtitle">
                {t("pickOneToBuildOnEitherReuseAnExistingTableOrLetTheEngineSuggestASmartStartingPoint")}
            </Text>

            <Space className="button-row">
                <Button
                    type={isSmartMode ? NewFallbackButtonType.Primary : NewFallbackButtonType.Default}
                    onClick={handleCustomModeClick}
                >
                    {t("createACustomTable")}
                </Button>
                <Button
                    type={isExistingMode ? NewFallbackButtonType.Primary : NewFallbackButtonType.Default}
                    onClick={handleExistingModeClick}
                >
                    {t("useExistingTable")}
                </Button>
            </Space>

            {isExistingMode && (
                <>
                    <div className="base-fallback-label">{t("baseFallbackOnTable")}</div>
                    <Select
                        className="select-for-create-new-draft-fallback"
                        placeholder={t("Select")}
                        value={scenarioName || undefined}
                        onChange={handleScenarioChange}
                    >
                        {tablesToShow?.map((scenario) => (
                            <Option key={scenario.name} value={scenario.id}>
                                {scenario.name}
                            </Option>
                        ))}
                    </Select>
                </>
            )}

            <Space className="button-row">
                <Button onClick={handleBackClick}>
                    {t("Back")}
                </Button>
                <Button
                    className="create-button-for-create-new-draft-fallback"
                    onClick={handleCreate}
                    disabled={!mode || (isExistingMode && !selectedRangeParentKey)}
                >
                    {t("Create")}
                </Button>
            </Space>
        </div>
    </PageContainerForCreateNewDraftFallback>
    );
};
